import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import FetchModelAssignments from '@Application/Ai/AiConfiguration/Assignments/Queries/FetchModelAssignments';
import SaveUsageSettings from '@Application/Ai/AiConfiguration/Assignments/Commands/SaveUsageSettings';
import type { UsageSettingsSaveFailedAction } from '@Application/Ai/AiConfiguration/Assignments/Actions/usageSettingsSaveFailed';
import fetchModelAssignments from '@Application/Ai/AiConfiguration/Assignments/fetchModelAssignments';
import saveUsageSettings from '@Application/Ai/AiConfiguration/Assignments/saveUsageSettings';
import { PermissionEnum } from '@Application/Security/Roles';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import UsageSettingsFormData from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/FormData/UsageSettingsFormData';
import HintText from '@Guidelines/Component/HintText';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Skeleton, TextArea } from '@verclam/ui-kit';

const detailsStyle = css`
	padding: 1.125rem 1.375rem;
	border-radius: ${theme.radii.xlarge};
	border: 1.5px solid ${theme.colors.border};
	background-color: ${theme.colors.surface};
`;

const summaryStyle = css`
	${theme.typography.eyebrow}
	cursor: pointer;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
	line-height: 1.6;
`;

const contentStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
	padding-top: 0.875rem;
`;

const footerStyle = css`
	display: flex;
	justify-content: flex-end;
`;

type Props = {
	readonly organizationId: number;
	/** The AI usage the transcription runs under: `interview_transcription` or `dictation`. */
	readonly usage: string;
};

/**
 * The vocabulary of a transcription, kept in the organization's per-usage call settings. Only the
 * vocabulary is exposed: the temperature is an account-level setting. An empty field means "inherit",
 * and the context prompt is sent back as the organization's own value, never the inherited one.
 * Hidden without `AI_ASSIGNMENT_READ`, read-only without `AI_ASSIGNMENT_WRITE`.
 */
const VocabularySection: React.FC<Props> = ({ organizationId, usage }) => {
	const dispatch = useDispatch();
	const canRead = useHasPermission(PermissionEnum.AI_ASSIGNMENT_READ);
	const canWrite = useHasPermission(PermissionEnum.AI_ASSIGNMENT_WRITE);
	const scope = React.useMemo(() => ({ type: 'organization' as const, organizationId }), [organizationId]);
	const [formData, setFormData] = React.useState(() => UsageSettingsFormData.createEmpty());
	const [isSaving, setIsSaving] = React.useState(false);

	const {
		data: assignments,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.organizationAssignments,
		stopOn: (value) =>
			!canRead || (!!value && value.scopeType === 'organization' && value.scopeId === organizationId),
		query: () => {
			if (!canRead) {
				return Promise.resolve();
			}

			return dispatch(fetchModelAssignments(new FetchModelAssignments(scope)));
		},
		deps: [organizationId, canRead],
	});

	const entry = React.useMemo(
		() =>
			assignments !== null &&
			assignments.scopeType === 'organization' &&
			assignments.scopeId === organizationId
				? (assignments.list.find((item) => item.usage.value === usage) ?? null)
				: null,
		[assignments, organizationId, usage],
	);

	React.useEffect(() => {
		if (entry !== null) {
			setFormData(UsageSettingsFormData.fromView(entry, scope));
		}
	}, [entry, scope]);

	const handleVocabularyChange = React.useCallback((value: string) => {
		setFormData((current) =>
			current.setVocabularyTerms(value).setIsVocabularyOverridden(value.trim() !== ''),
		);
	}, []);

	const handleSaveClick = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		setIsSaving(true);

		dispatch(saveUsageSettings(new SaveUsageSettings(scope, usage, formData.toCommandData())))
			.catch((action: UsageSettingsSaveFailedAction) => {
				updateValidationFromExternalSource({
					action,
					eventNames: 'USAGE_SETTINGS_SAVE_FAILED',
					formDataUpdater: setFormData,
				});
				ignoreHandledError();
			})
			.finally(() => setIsSaving(false));
	}, [dispatch, formData, scope, usage]);

	if (!canRead) {
		return null;
	}

	if (entry === null && error === null) {
		return <Skeleton height="4rem" borderRadius={theme.radii.xlarge} animation="wave" />;
	}

	if (entry === null) {
		return <ErrorState title="Impossible de charger les réglages avancés." onRetry={refetch} />;
	}

	const setting = entry.settings.vocabularyTerms;

	if (!setting.applicable) {
		return null;
	}

	const isEditable = canWrite && setting.editable && setting.supported;
	const showsInheritedValue = setting.own === null;
	const inheritedTerms = setting.effective ?? [];

	return (
		<React.Fragment>
			<details css={detailsStyle}>
				<summary css={summaryStyle}>Réglages avancés</summary>
				<div css={contentStyle}>
					<TextArea
						name="vocabularyTerms"
						label="Vocabulaire"
						subLabel={
							setting.supported
								? 'Un terme par ligne.'
								: (setting.unsupportedReason ?? undefined)
						}
						rows={2}
						placeholder="Hérité"
						value={formData.vocabularyTerms}
						onChange={handleVocabularyChange}
						disabled={!isEditable}
						errorMessage={formData.getValidationMessage('vocabularyTerms')}
						isFormFieldValid={formData.isValid('vocabularyTerms')}
					/>
					<ConditionalLoader data={showsInheritedValue && inheritedTerms.length > 0}>
						<HintText>
							Valeur héritée
							{setting.effectiveLevel !== null
								? ` (${setting.effectiveLevel.label.toLowerCase()})`
								: ''}{' '}
							: {inheritedTerms.join(', ')}.
						</HintText>
					</ConditionalLoader>
					<HintText>Laissé vide, ce réglage reprend la valeur héritée.</HintText>
				</div>
			</details>
			<ConditionalLoader data={isEditable}>
				<div css={footerStyle}>
					<Button type="button" label="Enregistrer" loading={isSaving} onClick={handleSaveClick} />
				</div>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default VocabularySection;
